import React, { useEffect, useRef, useState } from "react";
import { Box, Stack } from "@mui/material";
import { SegmentSubTitle, SegmentTitleEmoji } from "../SegmentSettings.styled";
import { SegmentTitleTextField } from "../../ContentBlocksEditor/children/ContentBlocksHeader/ContentBlocksHeader.styled";
import { ASSETS } from "../../../utils/config";
import EmojiPicker from "emoji-picker-react";
import { Close } from "@mui/icons-material";
import { isUnnamedSegmentTitle } from "../../../utils/segmentLabels";

const SegmentTitle = ({
  onTitleChange,
  title,
  // DL-029: optional greyed placeholder (the computed "Segment N" for unnamed
  // segments). Defaults to today's copy so unset callers are byte-identical.
  placeholder = "Add title here",
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // xe-0914-dev-allengines-segment-title-drops-typed-characters: the parent
  // (SegmentSettings/index.jsx) feeds `title` through computeSegmentLabel(),
  // which recomputes a "Segment N" placeholder on every render while the
  // segment is still "unnamed" (empty title, or a title matching
  // /^Segment \d+$/ -- see utils/segmentLabels.js). Redux's own dispatch from
  // OUR onChange lands a render tick behind the keystroke that caused it, so
  // this field used to be `value={title}` (fully controlled by that prop):
  // mid-fast-typing, a re-render carrying the still-stale placeholder would
  // force the textarea's DOM value back to "Segment N", clobbering/interleaving
  // whatever the user had already typed (measured live: leading characters
  // silently dropped on webkit/chromium/firefox at 45ms keystroke cadence).
  // Fix: buffer the value locally and only resync from the incoming `title`
  // prop while the field is NOT focused (segment switch, external update) --
  // never while the user is actively editing it.
  const [localValue, setLocalValue] = useState(title || "");
  const lastEmittedRef = useRef(title || "");
  const isFocusedRef = useRef(false);
  // T22.121: the latest `title` prop, so blur can pick up an outside change
  // (a co-editor's winning title) that the focus guard below skipped.
  const latestTitleRef = useRef(title || "");
  latestTitleRef.current = title || "";

  useEffect(() => {
    if (isFocusedRef.current) return;
    if (title === lastEmittedRef.current) return;
    lastEmittedRef.current = title || "";
    setLocalValue(title || "");
  }, [title]);

  const emojiPickerRef = useRef(null);
  const emojiIconRef = useRef(null);

  const handleEmojiSelect = (emoji) => {
    // Get the current input element
    const input = document.getElementById("title-with-emoji");

    // Get the current text from the input
    const text = localValue;

    // Get the current cursor position
    const startPos = input.selectionStart;
    const endPos = input.selectionEnd;

    // Insert the emoji at the current cursor position
    const newText =
      localValue.length + emoji.length <= 75
        ? text.substring(0, startPos) +
          emoji +
          text.substring(endPos, text.length)
        : localValue;

    lastEmittedRef.current = newText;
    setLocalValue(newText);
    // Make sure to update the Formik field value as well
    onTitleChange(newText);

    // Set the cursor position right after the inserted emoji
    setTimeout(() => {
      input.selectionStart = input.selectionEnd = startPos + emoji.length;
    }, 0);
  };

  const handleTextChange = (event) => {
    const currentValue = event.target.value;
    const newValue = currentValue.length <= 75 ? currentValue : localValue;

    lastEmittedRef.current = newValue;
    setLocalValue(newValue);
    onTitleChange(newValue);
  };

  const handleOutsideClick = (event) => {
    if (
      emojiPickerRef.current &&
      !emojiPickerRef.current.contains(event.target) &&
      (!emojiIconRef.current || !emojiIconRef.current.contains(event.target))
    ) {
      setShowEmojiPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <>
      <Stack gap={"10px"}>
        <SegmentSubTitle>Segment Title</SegmentSubTitle>

        <Stack gap={"5px"} direction={"row"} alignItems={"center"}>
          {/* ISP-9999 (#511): autoFocus made this field claim :focus-visible the
              instant the Segment Settings pane mounts — no user interaction —
              which is what feeds the app-wide purple focus ring
              (src/styles/focusRing.css). Removed so the field only shows a
              focus indicator when the user actually focuses it. */}
          <SegmentTitleTextField
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            inputProps={{
              "aria-label": "Segment Title",
            }}
            placeholder={placeholder}
            value={localValue}
            name="title"
            id="title-with-emoji"
            onChange={handleTextChange}
            onFocus={() => {
              isFocusedRef.current = true;
            }}
            onBlur={() => {
              isFocusedRef.current = false;
              const latest = latestTitleRef.current;
              if (latest === lastEmittedRef.current) return;
              // The parent sends "" for an unnamed title ("Segment 5", spaces):
              // that is our own text, not a co-editor's change.
              if (latest === "" && isUnnamedSegmentTitle(lastEmittedRef.current))
                return;
              lastEmittedRef.current = latest;
              setLocalValue(latest);
            }}
            fullWidth
            fontWeightValue="500"
            fontSizeValue="14px"
            backgroundColor="#F6F7F9"
            multiline
            // ISP-9999: a 75-char title (the field's own cap, see
            // handleTextChange) live-measures at up to 3 wrapped rows at this
            // field's rendered width — the old 2-row cap clipped the 3rd
            // line with no visible scroll affordance. 4 rows gives headroom.
            maxRows={4}
          />

          <SegmentTitleEmoji
            ref={emojiIconRef}
            src={ASSETS.emoji}
            onClick={() => {
              setShowEmojiPicker((prevState) => !prevState);
            }}
          />
        </Stack>
      </Stack>

      {showEmojiPicker ? (
        <Box
          ref={emojiPickerRef}
          style={{
            position: "fixed",
            transform: "translate(-50%, -50%)",
            top: "50vh",
            left: "10vw",
            display: "flex",
            flexDirection: "row",
            zIndex: 10000000,
          }}
        >
          <EmojiPicker
            onEmojiClick={(e) => handleEmojiSelect(e.emoji)}
            theme="light"
            autoFocusSearch={false}
          />
          <Box
            onClick={() => setShowEmojiPicker(false)}
            style={{
              // backgroundColor: "#123051",
              borderRadius: "12.5px",
              cursor: "pointer",
              position: "absolute",
              right: 15,
              top: 28,
              height: "25px",
              width: "25px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Close
              style={{
                fontSize: "25px",
                color: "#123051",
              }}
            />
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default SegmentTitle;
